import { StudentSimulationState } from '../../../types/v2';

interface PreviewSession {
  state: StudentSimulationState;
  submit: (actionId: string, expectedRevision: number) => Promise<{
    duplicate: boolean;
    simulation: StudentSimulationState;
  }>;
}

const conditionsMatch = (conditions: any[], facts: Record<string, any>): boolean => (
  conditions.every((condition) => (
    condition?.operator === 'eq' && facts[condition?.field] === condition?.value
  ))
);

export const createSimulationPreviewSession = (
  stepId: number,
  content: any,
  signalConfig: any,
): PreviewSession | null => {
  const simulation = content?.simulation;
  const presentation = simulation?.presentation;
  const rules = signalConfig?.simulation_rules;
  if (!simulation?.case_id || !presentation || !rules) return null;

  const facts = Object.fromEntries(
    Object.entries(rules.facts || {}).map(([id, definition]: [string, any]) => [id, definition?.initial]),
  );
  const evidence = new Map<string, string>();
  let revision = 0;
  let phase = String(rules.initial_phase || 'inspect');
  let status = 'in_progress';
  let promptingLevel = 0;
  let safetyFlag = false;
  let verification: any = { status: 'not_attempted', result_code: null, updated_at: null };
  let recentActions: any[] = [];
  let unsafeEvents: any[] = [];
  let lastResult: any = null;
  let completedAt: string | null = null;

  const serialize = (): StudentSimulationState => ({
    schema_version: 1,
    step_id: stepId,
    case_id: simulation.case_id,
    revision,
    phase,
    status,
    prompting_level: promptingLevel,
    safety_flag: safetyFlag,
    render_state: Object.fromEntries(
      (rules.render_state_fields || []).map((field: string) => [field, facts[field]]),
    ),
    evidence_captured: Array.from(evidence.entries()).map(([id, capturedAt]) => ({
      id,
      label: presentation.evidence_labels?.[id] || id,
      captured_at: capturedAt,
    })),
    verification: { ...verification },
    recent_actions: recentActions.map((item) => ({ ...item })),
    unsafe_events: unsafeEvents.map((item) => ({ ...item })),
    last_result: lastResult ? { ...lastResult } : null,
    completed_at: completedAt,
    presentation,
  });

  const session: PreviewSession = {
    state: serialize(),
    submit: async (actionId, expectedRevision) => {
      if (expectedRevision !== revision) {
        throw new Error('Simulation preview state changed. Try that action again.');
      }
      if (status === 'completed') {
        throw new Error('This simulation preview is already complete.');
      }

      const matching = (rules.transitions || []).filter((item: any) => item?.action_id === actionId);
      const transition = matching.find((item: any) => (
        Array.isArray(item.when) && item.when.length > 0 && conditionsMatch(item.when, facts)
      )) || matching.find((item: any) => !Array.isArray(item.when) || item.when.length === 0);
      if (!transition) throw new Error('That action is not available in the current preview state.');

      const occurredAt = new Date().toISOString();
      const effects = transition.effects || {};
      Object.entries(effects.set || {}).forEach(([field, value]) => {
        facts[field] = value;
      });
      if (effects.set_phase && effects.set_phase !== 'complete') phase = effects.set_phase;
      (effects.capture_evidence || []).forEach((id: string) => {
        if (!evidence.has(id)) evidence.set(id, occurredAt);
      });
      const unsafe = Boolean(effects.record_unsafe);
      if (unsafe) safetyFlag = true;
      if (effects.set_verification) {
        verification = {
          status: effects.set_verification,
          result_code: transition.result_code,
          updated_at: occurredAt,
        };
      }
      if (effects.set_prompting_level !== undefined) {
        promptingLevel = Number(effects.set_prompting_level) || 0;
      }

      revision += 1;
      lastResult = {
        action_id: actionId,
        sequence: revision,
        revision,
        result_code: transition.result_code,
        message: presentation.result_messages?.[transition.result_code] || '',
        unsafe,
        occurred_at: occurredAt,
      };
      recentActions = [...recentActions, lastResult].slice(-40);
      if (unsafe) unsafeEvents = [...unsafeEvents, lastResult];

      const completion = rules.completion || {};
      const hasEvidence = (completion.required_evidence || []).every((id: string) => evidence.has(id));
      const hasFacts = conditionsMatch(completion.facts || [], facts);
      if (hasEvidence && hasFacts && verification.status === completion.verification) {
        status = 'completed';
        phase = 'complete';
        completedAt = occurredAt;
      }

      session.state = serialize();
      return { duplicate: false, simulation: session.state };
    },
  };
  return session;
};
